import apiClient from './client';
import type {
  Equipment,
  EquipmentReport,
  EquipmentTypeRecord,
  LocationRecord,
  CompanyRecord,
  RepairRequest,
  RepairRequestNote,
  PmType,
  PmDataSource,
  PmAssignment,
  OrderedPart,
  PartAttachment,
  Vendor,
  User,
  UserRole,
  AuditLogEntry,
  CalendarEvent,
  PaginatedResponse,
  ListParams,
  CompletePmPayload,
} from '@/types';

// ─── Locations ───────────────────────────────────────────────────────────────

export const locationsApi = {
  list: () =>
    apiClient.get<LocationRecord[]>('/locations').then((r) => r.data),

  create: (data: { name: string }) =>
    apiClient.post<LocationRecord>('/locations', data).then((r) => r.data),

  update: (id: number, data: { name?: string; isActive?: boolean }) =>
    apiClient.put<LocationRecord>(`/locations/${id}`, data).then((r) => r.data),
};

// ─── Companies (managed list) ─────────────────────────────────────────────────

export const companiesListApi = {
  list: () =>
    apiClient.get<CompanyRecord[]>('/companies-list').then((r) => r.data),

  create: (data: { name: string }) =>
    apiClient.post<CompanyRecord>('/companies-list', data).then((r) => r.data),

  update: (id: number, data: { name?: string; isActive?: boolean }) =>
    apiClient.put<CompanyRecord>(`/companies-list/${id}`, data).then((r) => r.data),
};

// ─── Equipment Types ─────────────────────────────────────────────────────────

export const equipmentTypesApi = {
  list: () =>
    apiClient.get<EquipmentTypeRecord[]>('/equipment-types').then((r) => r.data),

  create: (data: { name: string }) =>
    apiClient.post<EquipmentTypeRecord>('/equipment-types', data).then((r) => r.data),

  update: (id: number, data: { name?: string; isActive?: boolean }) =>
    apiClient.put<EquipmentTypeRecord>(`/equipment-types/${id}`, data).then((r) => r.data),
};

// ─── Companies ───────────────────────────────────────────────────────────────

export const companiesApi = {
  list: () =>
    apiClient.get<string[]>('/companies').then((r) => r.data),
};

// ─── Equipment ────────────────────────────────────────────────────────────────

export const equipmentApi = {
  list: (params?: ListParams) =>
    apiClient
      .get<PaginatedResponse<Equipment>>('/equipment', { params })
      .then((r) => r.data),

  create: (data: Partial<Equipment>) =>
    apiClient.post<Equipment>('/equipment', data).then((r) => r.data),

  get: (id: string) =>
    apiClient.get<Equipment>(`/equipment/${id}`).then((r) => r.data),

  update: (id: string, data: Partial<Equipment>) =>
    apiClient.put<Equipment>(`/equipment/${id}`, data).then((r) => r.data),

  patchStatus: (id: string, status: string) =>
    apiClient
      .patch<Equipment>(`/equipment/${id}/status`, { assetStatus: status })
      .then((r) => r.data),

  getPhotoUploadUrl: (id: number, contentType: string) =>
    apiClient
      .get<{ uploadUrl: string; key: string }>(`/equipment/${id}/photo-upload-url`, { params: { contentType } })
      .then((r) => r.data),

  savePhotoKey: (id: number, key: string) =>
    apiClient
      .post<Equipment>(`/equipment/${id}/photo-key`, { key })
      .then((r) => r.data),

  getReport: (id: number) =>
    apiClient
      .get<EquipmentReport>(`/equipment/${id}/report`)
      .then((r) => r.data),
};

// ─── Repair Requests ──────────────────────────────────────────────────────────

export const repairRequestsApi = {
  list: (params?: ListParams) =>
    apiClient
      .get<PaginatedResponse<RepairRequest>>('/repair-requests', { params })
      .then((r) => r.data),

  create: (data: Record<string, unknown>) =>
    apiClient.post<RepairRequest>('/repair-requests', data).then((r) => r.data),

  get: (id: string) =>
    apiClient
      .get<RepairRequest>(`/repair-requests/${id}`)
      .then((r) => r.data),

  update: (id: string, data: Record<string, unknown>) =>
    apiClient
      .put<RepairRequest>(`/repair-requests/${id}`, data)
      .then((r) => r.data),

  patchStatus: (id: string, status: string) =>
    apiClient
      .patch<RepairRequest>(`/repair-requests/${id}/status`, { status })
      .then((r) => r.data),

  getCalendar: (start: string, end: string) =>
    apiClient
      .get<CalendarEvent[]>('/repair-requests/calendar', {
        params: { start, end },
      })
      .then((r) => r.data),

  getNotes: (id: string | number) =>
    apiClient
      .get<RepairRequestNote[]>(`/repair-requests/${id}/notes`)
      .then((r) => r.data),

  addNote: (id: string | number, note: string) =>
    apiClient
      .post<RepairRequestNote>(`/repair-requests/${id}/notes`, { note })
      .then((r) => r.data),
};

// ─── PM Types ─────────────────────────────────────────────────────────────────

export const pmTypesApi = {
  list: () =>
    apiClient.get<PmType[]>('/pm-types').then((r) => r.data),

  create: (data: Record<string, unknown>) =>
    apiClient.post<PmType>('/pm-types', data).then((r) => r.data),

  update: (id: number, data: Record<string, unknown>) =>
    apiClient.put<PmType>(`/pm-types/${id}`, data).then((r) => r.data),
};

// ─── PM Data Sources ──────────────────────────────────────────────────────────

export const pmDataSourcesApi = {
  list: () =>
    apiClient.get<PmDataSource[]>('/pm-data-sources').then((r) => r.data),

  create: (data: Partial<PmDataSource>) =>
    apiClient.post<PmDataSource>('/pm-data-sources', data).then((r) => r.data),

  update: (id: number, data: Partial<PmDataSource>) =>
    apiClient.put<PmDataSource>(`/pm-data-sources/${id}`, data).then((r) => r.data),
};

// ─── PM Assignments ───────────────────────────────────────────────────────────

export const pmAssignmentsApi = {
  list: (params?: { equipmentId?: number; active?: boolean }) =>
    apiClient.get<PmAssignment[]>('/pm-assignments', { params }).then((r) => r.data),

  get: (id: number) =>
    apiClient.get<PmAssignment>(`/pm-assignments/${id}`).then((r) => r.data),

  create: (data: Record<string, unknown>) =>
    apiClient.post<PmAssignment>('/pm-assignments', data).then((r) => r.data),

  update: (id: number, data: Record<string, unknown>) =>
    apiClient.put<PmAssignment>(`/pm-assignments/${id}`, data).then((r) => r.data),

  deactivate: (id: number) =>
    apiClient.delete<{ message: string }>(`/pm-assignments/${id}`).then((r) => r.data),

  complete: (id: number, data: CompletePmPayload) =>
    apiClient.post<{ assignment: PmAssignment }>(`/pm-assignments/${id}/complete`, data).then((r) => r.data),

  createRepairOrder: (id: number) =>
    apiClient.post<{ repairRequest: RepairRequest }>(`/pm-assignments/${id}/create-repair-order`).then((r) => r.data),
};

// ─── Ordered Parts ────────────────────────────────────────────────────────────

export const orderedPartsApi = {
  list: (params?: ListParams) =>
    apiClient
      .get<PaginatedResponse<OrderedPart>>('/ordered-parts', { params })
      .then((r) => r.data),

  create: (data: Record<string, unknown>) =>
    apiClient.post<OrderedPart>('/ordered-parts', data).then((r) => r.data),

  update: (id: string, data: Partial<OrderedPart>) =>
    apiClient
      .put<OrderedPart>(`/ordered-parts/${id}`, data)
      .then((r) => r.data),

  return: (id: string) =>
    apiClient
      .post<OrderedPart>(`/ordered-parts/${id}/return`)
      .then((r) => r.data),

  listAttachments: (id: number) =>
    apiClient
      .get<PartAttachment[]>(`/ordered-parts/${id}/attachments`)
      .then((r) => r.data),

  presignAttachment: (id: number, fileName: string, contentType: string) =>
    apiClient
      .post<{ uploadUrl: string; s3Key: string }>(`/ordered-parts/${id}/attachments/presign`, {
        fileName,
        contentType,
      })
      .then((r) => r.data),

  recordAttachment: (id: number, s3Key: string, fileName: string) =>
    apiClient
      .post(`/ordered-parts/${id}/attachments`, { s3Key, fileName })
      .then((r) => r.data),
};

// ─── Vendors ──────────────────────────────────────────────────────────────────

export const vendorsApi = {
  list: () =>
    apiClient.get<Vendor[]>('/vendors').then((r) => r.data),

  create: (data: Partial<Vendor>) =>
    apiClient.post<Vendor>('/vendors', data).then((r) => r.data),

  update: (id: string, data: Partial<Vendor>) =>
    apiClient.put<Vendor>(`/vendors/${id}`, data).then((r) => r.data),
};

// ─── Users ────────────────────────────────────────────────────────────────────

export const usersApi = {
  list: () =>
    apiClient.get<User[]>('/users').then((r) => r.data),

  updateRole: (id: number, role: UserRole) =>
    apiClient.put<User>(`/users/${id}/role`, { role }).then((r) => r.data),

  assignVendor: (id: number, vendorId: number | null) =>
    apiClient.put<User>(`/users/${id}/vendor`, { vendorId }).then((r) => r.data),

  updatePhone: (id: number, phoneNumber: string | null) =>
    apiClient.patch<User>(`/users/${id}/phone`, { phoneNumber }).then((r) => r.data),

  sync: () =>
    apiClient.post<User>('/users/sync').then((r) => r.data),

  invite: (data: { email: string; fullName: string; role: UserRole }) =>
    apiClient.post<{ message: string; user: User }>('/users/invite', data).then((r) => r.data),

  setActive: (id: number, isActive: boolean) =>
    apiClient.patch<User>(`/users/${id}/active`, { isActive }).then((r) => r.data),

  resendInvite: (id: number) =>
    apiClient.post<{ message: string }>(`/users/${id}/resend-invite`).then((r) => r.data),

  resetPassword: (id: number) =>
    apiClient.post<{ message: string }>(`/users/${id}/reset-password`).then((r) => r.data),

  delete: (id: number) =>
    apiClient.delete<{ message: string }>(`/users/${id}`).then((r) => r.data),

  recordConsent: (data: { termsAcceptedAt: string; smsConsent: boolean }) =>
    apiClient.patch<{ ok: boolean }>('/users/me/consent', data).then((r) => r.data),
};

// ─── Settings ─────────────────────────────────────────────────────────────────

export const settingsApi = {
  get: () =>
    apiClient.get<Record<string, string | null>>('/settings').then((r) => r.data),

  patch: (data: { timezone?: string }) =>
    apiClient.patch<Record<string, string | null>>('/settings', data).then((r) => r.data),
};

// ─── Audit Log ────────────────────────────────────────────────────────────────

export const auditLogApi = {
  list: (params?: { table?: string; recordId?: number; action?: string; limit?: number; offset?: number }) =>
    apiClient
      .get<{ items: AuditLogEntry[]; limit: number; offset: number }>('/audit-log', { params })
      .then((r) => r.data),
};

// ─── Notifications ────────────────────────────────────────────────────────────

export interface NotifSubscription {
  id: number;
  eventKey: string;
  targetType: 'user' | 'role';
  userId: number | null;
  targetRole: string | null;
  channel: 'email' | 'sms' | 'both';
  createdAt: string;
  user: { id: number; fullName: string; email: string; phoneNumber: string | null } | null;
  roleName: string | null;
}

export interface NotifConfig {
  subscriptions: NotifSubscription[];
  toggles: Record<string, boolean>;
}

export const notificationsApi = {
  get: () =>
    apiClient.get<NotifConfig>('/notifications/subscriptions').then((r) => r.data),

  addSubscription: (data: {
    eventKey: string;
    targetType: 'user' | 'role';
    userId?: number;
    targetRole?: string;
    channel: 'email' | 'sms' | 'both';
  }) =>
    apiClient.post<NotifSubscription>('/notifications/subscriptions', data).then((r) => r.data),

  removeSubscription: (id: number) =>
    apiClient.delete(`/notifications/subscriptions/${id}`).then((r) => r.data),

  patchToggle: (key: string, value: boolean) =>
    apiClient.patch<{ ok: boolean }>('/notifications/toggles', { key, value }).then((r) => r.data),
};

// ─── Calendar ─────────────────────────────────────────────────────────────────

export const calendarApi = {
  get: (start: string, end: string) =>
    apiClient
      .get<CalendarEvent[]>('/calendar', { params: { start, end } })
      .then((r) => r.data),
};
